/**
 * Tipe respons Admin API, mengikuti kontrak HTTP di
 * `local://tahap3-admin-api.md` dan tipe Go di `internal/adminquery`,
 * `internal/manage`, `internal/adminauth`, dan `internal/webhook`.
 *
 * Nama field memakai snake_case persis seperti yang dikirim server.
 */

export type Role = 'admin' | 'viewer'
export type UserStatus = 'ACTIVE' | 'DISABLED'
export type RowStatus = 'ACTIVE' | 'DISABLED'

export type PaymentStatus =
  | 'PENDING'
  | 'PAID'
  | 'EXPIRED'
  | 'FAILED'
  | 'REFUNDED'
  | 'CREATING'

export type DeliveryStatus = 'PENDING' | 'DELIVERED' | 'DEAD'

/** Kanal pembayaran: QRIS MPM atau Virtual Account On Us. */
export type PaymentMethod = 'qris' | 'va'

/** Urutan status pada state machine, dipakai untuk daftar filter dan dashboard. */
export const PAYMENT_STATUSES: PaymentStatus[] = [
  'PENDING',
  'PAID',
  'EXPIRED',
  'FAILED',
  'REFUNDED',
  'CREATING',
]

export type ServiceMeta = {
  service: string
  version: string
  commit: string
  build_date: string
  environment: string
}

export type User = {
  id: string
  email: string
  name: string
  role: Role
  status: UserStatus
  merchant_ids: string[]
  merchant_scope_restricted: boolean
  totp_enabled: boolean
  last_login_at: string | null
  created_at: string
  updated_at: string
}

export type SessionResponse = {
  user: User
  csrf_token: string
  expires_at: string
  totp_enrollment_required: boolean
  totp_required: boolean
}

export type TotpEnrollment = { secret: string; otpauth_uri: string }

export type Page<T> = {
  data: T[]
  next_cursor: string
}

export type DataList<T> = {
  data: T[]
}

export type PaymentSummary = {
  id: string
  client: string
  merchant: string
  provider: string
  environment: string
  method: PaymentMethod
  va_number: string
  client_reference: string
  attempt_no: number
  amount_minor: number
  paid_amount_minor: number | null
  status: PaymentStatus
  status_version: number
  partner_reference_no: string
  provider_reference_no: string
  rrn: string
  needs_review: boolean
  review_reason: string
  paid_after_final: boolean
  failure_reason: string
  expires_at: string
  paid_at: string | null
  created_at: string
  updated_at: string
}

export type PaymentEvent = {
  id: number
  type: string
  source: string
  from_status: string
  to_status: string
  data: unknown
  actor: string
  created_at: string
}

export type Exchange = {
  id: number
  credential: string
  direction: string
  operation: string
  method: string
  url: string
  request_headers: unknown
  request_body: unknown
  response_status: number | null
  response_body: unknown
  verification: string
  external_id: string
  duration_ms: number | null
  error: string
  created_at: string
}

export type DeliveryAttempt = {
  attempt_no: number
  status_code: number | null
  error: string
  duration_ms: number | null
  created_at: string
}

export type DeliveryView = {
  id: string
  payment_id: string
  event_type: string
  status: DeliveryStatus
  attempts: number
  next_attempt_at: string
  last_status_code: number | null
  last_error: string
  delivered_at: string | null
  created_at: string
  history: DeliveryAttempt[]
}

export type PaymentDetail = {
  payment: PaymentSummary
  description: string
  metadata: unknown
  qr_content: string
  next_poll_at: string | null
  events: PaymentEvent[]
  exchanges: Exchange[]
  deliveries: DeliveryView[]
}

export type StatusTotal = {
  status: PaymentStatus
  count: number
  amount_minor: number
}

export type Dashboard = {
  from: string
  to: string
  merchant: string
  by_status: StatusTotal[]
  needs_review: number
  webhook_dead: number
  webhook_pending: number
  inbound_rejected: number
}

export type DeliverySummary = {
  id: string
  client: string
  payment_id: string
  event_type: string
  status: DeliveryStatus
  attempts: number
  last_status_code: number | null
  last_error: string
  next_attempt_at: string
  delivered_at: string | null
  created_at: string
  updated_at: string
}

export type ClientKeyView = {
  key_id: string
  status: string
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
}

export type ClientView = {
  id: string
  slug: string
  name: string
  status: RowStatus
  webhook_url: string
  webhook_secret_set: boolean
  webhook_secret_rotating: boolean
  rate_limit_per_minute: number
  default_merchant: string
  merchants: string[]
  keys: ClientKeyView[]
  created_at: string
  updated_at: string
}

export type ClientCreated = {
  client: ClientView
  webhook_secret?: string
}

/**
 * Balasan PATCH client. `webhook_secret` hanya ada bila server baru membuat
 * secret, yaitu saat `webhook_url` diisi pada client yang belum punya secret.
 */
export type ClientUpdated = {
  client: ClientView
  webhook_secret?: string
}

export type KeyCreated = {
  key: ClientKeyView
  secret: string
}

export type WebhookSecretRotated = {
  secret: string
}

export type MerchantView = {
  id: string
  slug: string
  name: string
  status: RowStatus
  credential: string
  provider: string
  environment: string
  merchant_code: string
  bank_merchant_id: string
  sub_merchant_id: string
  store_id: string
  terminal_id: string
  nmid: string
  default_expiry_seconds: number
  min_expiry_seconds: number
  max_expiry_seconds: number
  settings: unknown
  created_at: string
  updated_at: string
}

export type CredentialView = {
  id: string
  slug: string
  provider: string
  environment: string
  status: RowStatus
  client_key: string
  client_secret_set: boolean
  private_key_set: boolean
  bank_public_key_set: boolean
  notify_secret_set: boolean
  settings: unknown
  inbound_token_url: string
  inbound_notify_url: string
  merchant_count: number
  created_at: string
  updated_at: string
}

export type AuditEntry = {
  id: number
  actor_user_id: string | null
  actor: string
  action: string
  target_type: string
  target_id: string
  changes: unknown
  ip: string
  request_id: string
  created_at: string
}

export type ReviewResolution = 'dismiss' | 'mark_paid'

export type SimulateOutcome = 'paid' | 'failed' | 'expired'

/* ---------- Masukan ---------- */

export type PaymentQuery = {
  status?: PaymentStatus[]
  merchant?: string
  client?: string
  from?: string
  to?: string
  client_reference?: string
  reference?: string
  needs_review?: boolean
  cursor?: string
  limit?: number
}

export type DeliveryQuery = {
  status?: DeliveryStatus
  client?: string
  cursor?: string
  limit?: number
}

export type RejectionQuery = {
  credential?: string
  from?: string
  to?: string
  cursor?: string
  limit?: number
}

export type AuditQuery = {
  actor?: string
  action?: string
  target_type?: string
  target_id?: string
  from?: string
  to?: string
  cursor?: string
  limit?: number
}

export type DashboardQuery = {
  from?: string
  to?: string
  merchant?: string
}

export type CreateClientInput = {
  slug: string
  name: string
  webhook_url: string
  merchants: string[]
  default_merchant: string
}

export type UpdateClientInput = {
  name?: string
  status?: RowStatus
  webhook_url?: string
  rate_limit_per_minute?: number
}

export type SetClientMerchantsInput = {
  merchants: string[]
  default_merchant: string
}

export type MerchantInput = {
  slug: string
  name: string
  credential: string
  merchant_code: string
  bank_merchant_id: string
  sub_merchant_id: string
  store_id: string
  terminal_id: string
  default_expiry_seconds: number
  min_expiry_seconds: number
  max_expiry_seconds: number
  settings: unknown
}

export type MerchantPatch = Partial<Omit<MerchantInput, 'slug' | 'credential'>> & {
  status?: RowStatus
}

export type CreateCredentialInput = {
  slug: string
  provider: string
  environment: string
  client_key: string
  client_secret: string
  private_key_pem: string
  bank_public_key_pem: string
  notify_secret: string
  settings: unknown
}

export type UpdateCredentialInput = {
  status?: RowStatus
  client_key?: string
  settings?: unknown
}

export type CredentialSecretsInput = {
  client_secret?: string
  private_key_pem?: string
  bank_public_key_pem?: string
  notify_secret?: string
}

export type CreateUserInput = {
  email: string
  name: string
  password: string
  role: Role
  merchant_ids?: string[]
  merchant_scope_restricted?: boolean
}

export type UpdateUserInput = {
  name?: string
  role?: Role
  status?: UserStatus
  password?: string
  merchant_ids?: string[]
  merchant_scope_restricted?: boolean
}

export type ResolveReviewInput = {
  resolution: ReviewResolution
  note: string
  paid_amount_minor?: number
}
